import React, { useMemo } from 'react';
import { AccessorKeyColumnDef, createColumnHelper, RowData } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { getDisplayName } from '../../../../utils/person';
import Table from './Table';
import { DeleteCell, InviteCell, VerifiedCell } from '../TableCells';
import { useUser } from '../../../../hooks/use-user';

export type DationInstanceUserDataEntry = {
	id: number;
	firstName: string;
	lastName: string;
	insertion: string;
	emailAddress: string;
	roles: string[];
	verified: boolean;
	dationInstance: {
		id: number;
		name: string;
	};
};

const columnHelper = createColumnHelper<DationInstanceUserDataEntry>();

type Props = {
	users: DationInstanceUserDataEntry[];
	handleDelete: (row: RowData) => void;
	handleRowClick: (row: RowData) => void;
	handleInvite: (row: RowData) => void;
};

const DationInstanceUserOverview = ({
	users,
	handleInvite,
	handleDelete,
	handleRowClick,
}: Props) => {
	const { t } = useTranslation();
	const { profile } = useUser();

	const columns = useMemo(
		() =>
			[
				columnHelper.display({
					id: 'name',
					cell: (cellInfo) => getDisplayName(cellInfo.row.original),
					header: t('dation_instance.name'),
				}),
				columnHelper.accessor('emailAddress', {
					header: t('portal.email_placeholder'),
				}),
				columnHelper.accessor('roles', {
					header: t('users.role'),
					cell: (cellInfo) =>
						cellInfo.row.original.roles.length > 0
							? t(`users.roles.${cellInfo.row.original.roles[0]?.toLowerCase()}`)
							: '',
				}),
				columnHelper.accessor('verified', {
					header: t('add_employee.verified'),
					cell: (cellInfo) => <VerifiedCell verified={cellInfo.cell.getValue()} />,
				}),
				columnHelper.display({
					id: 'invite',
					header: t('users.invite'),
					cell: (cellInfo) => (
						<InviteCell
							handleInvite={handleInvite}
							verified={cellInfo.row.original.verified}
							original={cellInfo.row.original}
							buttonText={t('users.resend_invite')}
						/>
					),
				}),
				columnHelper.display({
					id: 'delete',
					cell: (cellInfo) => (
						<DeleteCell
							handleDelete={handleDelete}
							original={cellInfo.row.original}
							isCurrentUser={profile.id === cellInfo.row.original.id}
						/>
					),
				}),
			] as Array<AccessorKeyColumnDef<DationInstanceUserDataEntry, unknown>>,
		[],
	);

	return <Table data={users} columns={columns} handleRowClick={handleRowClick} />;
};

export default DationInstanceUserOverview;
